import React from 'react';
import { Controller } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import { Checkbox } from '@planne-software/uni';
import { IconShieldCheckFilled } from '@planne-software/uni/icons';
import { Button } from '@planne-software/uni/Button';
import { Input } from '@planne-software/uni/Input';
import { PhoneInput } from '@planne-software/uni/PhoneInput';

import { useTranslate } from '@src/core/hooks';
import { IDENTITY_TYPE } from '@src/domains/customers';
import { AuthCardRoot, AuthCardSubtitle, AuthCardTextGroup, AuthCardTitle } from '@src/features/customers';

import { useSignupForm } from '../hooks/useSignupForm';
import { isEmailExistenceError, isEmailUniquenessError } from '../core/signupErrors';
import {
  CheckboxAlignRow,
  FieldErrorText,
  FieldGrid,
  FieldStack,
  RecaptchaWrapper,
  SecureFooter,
  SecureFooterText,
  SubmitButtonWrapper,
  SubmitFooter,
} from '../styles';
import { AddressFields } from './AddressFields';
import { EmailInUseAlert } from './EmailInUseAlert';
import { EmailNotFoundAlert } from './EmailNotFoundAlert';
import { ForeignIdentityFields } from './ForeignIdentityFields';
import { IdentityTypeSelector } from './IdentityTypeSelector';
import { MaskedField } from '@src/common/ui/MaskedField';
import { PasswordFields } from './PasswordFields';
import { TermsAcceptance } from './TermsAcceptance';

type SignupProps = {
  email: string;
  onLogin: () => void;
  onForgotPassword: (email: string) => void;
  onAccountCreated?: () => void;
};

export const Signup = (props: SignupProps) => {
  const { email, onLogin, onForgotPassword, onAccountCreated } = props;
  const { i18n } = useTranslate();

  const {
    control,
    errors,
    countryCode,
    identityType,
    isForeign,
    isCompany,
    canBeForeign,
    showAddress,
    recaptchaKey,
    loading,
    setValue,
    onIdentityTypeChange,
    onCountryCodeChange,
    onIamForeignChange,
    onRecoverPassword,
    onSubmit,
  } = useSignupForm({ initialEmail: email, onForgotPassword, onAccountCreated });

  const emailUniquenessError = isEmailUniquenessError(errors.email);
  // The alert explains the error, so the field only turns red instead of repeating the message below it.
  const emailExistenceError = isEmailExistenceError(errors.email);

  const phoneLabelKey = isCompany ? 'checkout_auth_signup_legal_phone_label' : 'checkout_auth_signup_phone_label';

  const phoneField = (
    <Controller
      name='phone'
      control={control}
      render={({ field: { onChange, onBlur, value, name }, fieldState }) => (
        <PhoneInput
          name={name}
          size='medium'
          label={i18n(phoneLabelKey)}
          value={value ?? ''}
          onChange={(next) => onChange(next.phone)}
          onBlur={onBlur}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          disabled={loading}
          required
          fullWidth
        />
      )}
    />
  );

  return (
    <AuthCardRoot data-testid='signup'>
      <AuthCardTextGroup>
        <AuthCardTitle>{i18n('checkout_auth_signup_title')}</AuthCardTitle>
        <AuthCardSubtitle>{i18n('checkout_auth_signup_subtitle')}</AuthCardSubtitle>
      </AuthCardTextGroup>

      <form onSubmit={onSubmit} noValidate autoComplete='off'>
        <FieldStack>
          <Controller
            name='email'
            control={control}
            render={({ field: { onChange, onBlur, value, name, ref }, fieldState }) => (
              <Input
                type='email'
                name={name}
                id={name}
                label={i18n('checkout_auth_email_label')}
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
                error={!!fieldState.error && !emailUniquenessError}
                helperText={emailUniquenessError || emailExistenceError ? undefined : fieldState.error?.message}
                disabled={loading}
                required
                autoComplete='email'
                ref={ref}
                size='medium'
                fullWidth
              />
            )}
          />

          {emailUniquenessError ? <EmailInUseAlert onLogin={onLogin} onRecoverPassword={onRecoverPassword} /> : null}

          {emailExistenceError ? <EmailNotFoundAlert /> : null}

          <IdentityTypeSelector value={identityType} onChange={onIdentityTypeChange} disabled={loading} />

          <FieldGrid>
            <Controller
              name='firstName'
              control={control}
              render={({ field: { onChange, onBlur, value, name, ref }, fieldState }) => (
                <Input
                  type='text'
                  name={name}
                  id={name}
                  label={
                    isCompany
                      ? i18n('checkout_auth_signup_legal_first_name_label')
                      : i18n('checkout_auth_signup_first_name_label')
                  }
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={loading}
                  required
                  ref={ref}
                  size='medium'
                  fullWidth
                />
              )}
            />

            <Controller
              name='lastName'
              control={control}
              render={({ field: { onChange, onBlur, value, name, ref }, fieldState }) => (
                <Input
                  type='text'
                  name={name}
                  id={name}
                  label={
                    isCompany
                      ? i18n('checkout_auth_signup_legal_last_name_label')
                      : i18n('checkout_auth_signup_last_name_label')
                  }
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={loading}
                  required
                  ref={ref}
                  size='medium'
                  fullWidth
                />
              )}
            />
          </FieldGrid>

          {isCompany ? (
            <FieldGrid>
              <Controller
                name='identity'
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState }) => (
                  <MaskedField
                    mask='cnpj'
                    name='identity'
                    label={i18n('checkout_auth_signup_cnpj_label')}
                    value={value ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    disabled={loading}
                    required
                    fullWidth
                  />
                )}
              />

              <Controller
                name='corporateName'
                control={control}
                render={({ field: { onChange, onBlur, value, name, ref }, fieldState }) => (
                  <Input
                    type='text'
                    name={name}
                    id={name}
                    label={i18n('checkout_auth_signup_corporate_name_label')}
                    value={value ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    disabled={loading}
                    required
                    ref={ref}
                    size='medium'
                    fullWidth
                  />
                )}
              />
            </FieldGrid>
          ) : null}

          {canBeForeign ? (
            <Controller
              name='iAmForeign'
              control={control}
              render={({ field: { value } }) => (
                <CheckboxAlignRow>
                  <Checkbox
                    name='iAmForeign'
                    title={i18n('checkout_auth_signup_foreign_checkbox')}
                    checked={Boolean(value)}
                    disabled={loading}
                    onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
                      onIamForeignChange(checked)
                    }
                    data-testid='signup-foreign-checkbox'
                  />
                </CheckboxAlignRow>
              )}
            />
          ) : null}

          {isForeign ? (
            <FieldGrid>
              <ForeignIdentityFields
                control={control}
                countryCode={countryCode}
                onCountryCodeChange={onCountryCodeChange}
                disabled={loading}
              />
            </FieldGrid>
          ) : identityType === IDENTITY_TYPE.CPF ? (
            <FieldGrid>
              <Controller
                name='identity'
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState }) => (
                  <MaskedField
                    mask='cpf'
                    name='identity'
                    label={i18n('checkout_auth_signup_cpf_label')}
                    value={value ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    disabled={loading}
                    required
                    fullWidth
                  />
                )}
              />

              {phoneField}
            </FieldGrid>
          ) : null}

          {identityType === IDENTITY_TYPE.CNPJ || isForeign ? phoneField : null}

          {showAddress ? <AddressFields control={control} isForeign={isForeign} disabled={loading} /> : null}

          <FieldGrid>
            <PasswordFields control={control} disabled={loading} />
          </FieldGrid>

          {recaptchaKey ? (
            <RecaptchaWrapper>
              <ReCAPTCHA sitekey={recaptchaKey} onChange={(token) => setValue('recaptchaToken', token)} />
              {errors.recaptchaToken ? <FieldErrorText>{errors.recaptchaToken.message}</FieldErrorText> : null}
            </RecaptchaWrapper>
          ) : null}

          <TermsAcceptance control={control} disabled={loading} />

          <SubmitFooter>
            <SecureFooter>
              <IconShieldCheckFilled size={20} aria-hidden />
              <SecureFooterText>{i18n('checkout_auth_signup_secure_purchase')}</SecureFooterText>
            </SecureFooter>

            <SubmitButtonWrapper>
              <Button type='submit' loading={loading} disabled={loading}>
                {i18n('checkout_auth_signup_confirm_cta')}
              </Button>
            </SubmitButtonWrapper>
          </SubmitFooter>
        </FieldStack>
      </form>
    </AuthCardRoot>
  );
};

export default Signup;
